import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { PaymentApi, PublicApi } from "../../api/services";
import { errorMessage } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { external } from "../../platform";
import { EmptyState, PageHeader, Spinner, StatusBadge } from "../../components/common";
import { dateTime, peso } from "../../lib/format";

export default function Membership() {
  const { data: cfg } = useQuery({ queryKey: ["public-config"], queryFn: PublicApi.config });
  const { data: m, isLoading } = useQuery({ queryKey: ["membership"], queryFn: PaymentApi.membership });
  const { data: payments } = useQuery({ queryKey: ["payments"], queryFn: PaymentApi.mine });
  const [busy, setBusy] = useState("");
  const { user } = useAuth();

  const join = async (planId: string) => {
    setBusy(planId);
    try { const { payment } = await PaymentApi.joinPlan(planId); external.open(payment.checkout_url); }
    catch (e) { toast.error(errorMessage(e)); setBusy(""); }
  };

  if (isLoading || !cfg) return <Spinner />;
  return (
    <div className="space-y-6">
      <PageHeader title="FASTPASS membership" subtitle="Prices in Philippine Peso (₱)." />
      {cfg.payments_simulated && (
        <p className="rounded-xl border border-dashed border-amber-400 bg-amber-50 p-3 text-sm text-amber-900" data-testid="payments-test-mode-notice">
          TEST MODE — payments are simulated. No real money is charged until PayMongo is connected.
        </p>
      )}
      {m?.active ? (
        <div className="fp-card border-[#036248] bg-emerald-50" data-testid="membership-active-card">
          <p className="fp-label">Active plan</p>
          <p className="mt-1 text-xl font-bold text-slate-900">{m.active.plan_name}</p>
          <p className="text-sm text-slate-700">{m.active.discount_pct}% off transport fares · valid until {dateTime(m.active.ends_at)}</p>
          {m.active.free_rides_total != null && <p className="mt-2 text-sm font-semibold text-[#003F32]" data-testid="membership-free-rides">{m.active.free_rides_left} of {m.active.free_rides_total} free rides left this year</p>}
          {m.active.insurance && <p className="text-sm text-slate-700" data-testid="membership-insurance">Covered by {m.active.insurance}</p>}
        </div>
      ) : user?.account_verified === false ? (
        <Link to="/patient/verify" className="fp-card block border-[#FFC400] bg-[#FFF4CC] text-[#003F32]" data-testid="membership-reserved-card">
          <p className="fp-label">Have a FastPass card?</p>
          <p className="mt-1 text-lg font-bold">Activate your membership</p>
          <p className="text-sm">Scan your package QR and card serial — includes 2 free rides a year and Philbritish Insurance. Tap here.</p>
        </Link>
      ) : <EmptyState title="You are not a member yet" body="Choose a plan below." testId="membership-none" />}

      <div className="grid gap-4 sm:grid-cols-2">
        {cfg.plans.map((p: any) => (
          <div key={p.id} className="fp-card flex flex-col" data-testid={`plan-card-${p.id}`}>
            <p className="font-display text-lg font-bold">{p.name}</p>
            <p className="mt-1 text-3xl font-extrabold text-[#036248]">{peso(p.price)}<span className="text-sm font-medium text-slate-500"> / {p.duration_days} days</span></p>
            <ul className="mt-3 flex-1 space-y-1.5 text-sm text-slate-700">
              {p.benefits.map((x: string) => <li key={x} className="flex gap-2"><Check size={16} className="mt-0.5 shrink-0 text-[#036248]" />{x}</li>)}
            </ul>
            <button className="fp-btn-yellow mt-4" onClick={() => join(p.id)} disabled={!!busy || m?.active?.plan_id === p.id || m?.active?.source === "card"} data-testid={`plan-join-${p.id}`}>
              {busy === p.id && <Loader2 size={14} className="animate-spin" />}{m?.active?.plan_id === p.id ? "Current plan" : "Join with QR Ph / GCash / Maya / card"}
            </button>
          </div>
        ))}
      </div>
      {cfg.sample_values && <p className="text-xs text-slate-500">Plan prices and benefits are sample values configured by FASTPASS admins.</p>}

      <section className="space-y-2" data-testid="payment-history">
        <h2 className="fp-label">Payment history</h2>
        {!payments?.length ? <EmptyState title="No payments yet" /> : payments.map((p) => (
          <div key={p.id} className="fp-card flex items-center justify-between gap-3 text-sm" data-testid="payment-row">
            <div><p className="font-medium">{p.description}</p><p className="text-xs text-slate-500">{dateTime(p.created_at)} · {p.provider === "simulated" ? "TEST payment" : "PayMongo"}</p></div>
            <div className="text-right"><p className="font-semibold">{peso(p.amount)}</p><StatusBadge status={p.status} /></div>
          </div>
        ))}
      </section>
    </div>
  );
}
